import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  apiClient,
  updateTask,
  updateTaskApproval,
  selectImagesForTask,
  updateHeroImage,
  getChildSocialPostCount,
  generateAiHeroImage,
  syncContentCalendar,
  getContentCalendarSyncStatus,
  previewCalendarResync,
  applyCalendarResync,
  repurposeArticle,
  getTask,
  getSchemaPreview,
  createQuickWinMetaData,
  SchemaPreviewResponse,
  RepurposeArticleResponse,
  ResyncPreview,
} from '../services/api';
import EditableArticleViewer from './EditableArticleViewer';
import SocialPublishModal from './SocialPublishModal';
import SchedulePublicationModal from './SchedulePublicationModal';
import EditableContentCalendar from './EditableContentCalendar';
import EditableSocialMediaViewer from './EditableSocialMediaViewer';
import EditableNewsletterViewer from './EditableNewsletterViewer';
import { GlassCard } from './ui/GlassCard';
import { GlassBadge } from './ui/GlassBadge';
import { GlassButton } from './ui/GlassButton';
import { Download, CalendarCheck, RefreshCw, Pencil, X, XCircle, Check, Plus, ExternalLink, ThumbsUp, ThumbsDown, RotateCcw, FileDown, CheckCircle, CheckCircle2, Clock, Circle, ImageIcon, Upload, Link, Loader2, Send, Share2, Wand2, ChevronDown, ChevronUp, Copy, Globe, Search, BookOpen } from 'lucide-react';
import './ContentPreview.css';
import InternalLinksPanel from './SEO/InternalLinksPanel';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

/** What the editors edit: the backend-unwrapped body when the API supplied one,
 *  otherwise the raw field. Never returns undefined. (content-editor-
 *  synthesized-unwrap fix, 2026-09 — see CLAUDE.md gotcha on synthesized_result
 *  storage shapes.) */
const editSourceOf = (t: any): string => {
  const clean = t?.synthesized_result_clean;
  if (typeof clean === 'string' && clean.length > 0) return clean;
  return t?.synthesized_result ?? '';
};

/** True when a string still carries a generator wrapper (i.e. is NOT yet
 *  unwrapped) — used as a save-time guard so a still-wrapped string can
 *  never be written back as the article body. */
const looksLikeWrappedGeneratorOutput = (s: string): boolean =>
  /^\{\s*['"](response|result|type)['"]\s*:/.test((s || '').trimStart());

interface ContentPreviewProps {}

// Related content types
interface RelatedTask {
  task_id: string;
  content_type: string;
  platforms: string[];
  quick_win_type: string;
}

interface RelatedSourceTask {
  task_id: string;
  name: string;
  normalized_title: string;
  approval_status?: string | null;
  related: RelatedTask[];
}

interface RelatedContentResponse {
  source_tasks: RelatedSourceTask[];
}

// Parse markdown table to structured data (handles empty lines between rows)
const parseMarkdownTable = (lines: string[], startIndex: number): { headers: string[], rows: string[][], endIndex: number } | null => {
  const headers: string[] = [];
  const rows: string[][] = [];
  let i = startIndex;

  // Parse header row
  const headerLine = lines[i]?.trim();
  if (!headerLine || !headerLine.startsWith('|')) return null;
  
  headers.push(...headerLine.split('|').map(h => h.trim()).filter(h => h));
  i++;

  // Skip empty lines before separator
  while (i < lines.length && lines[i]?.trim() === '') i++;

  // Parse separator row (|------|------|)
  const separatorLine = lines[i]?.trim();
  if (!separatorLine || !separatorLine.startsWith('|') || !separatorLine.includes('-')) return null;
  i++;

  // Parse data rows (skip empty lines between rows)
  while (i < lines.length) {
    const line = lines[i]?.trim();
    
    // Skip empty lines
    if (line === '') {
      i++;
      continue;
    }
    
    // Stop if not a table row
    if (!line.startsWith('|')) break;
    
    const cells = line.split('|').map(c => c.trim()).filter(c => c);
    if (cells.length > 0) {
      rows.push(cells);
    }
    i++;
  }

  return { headers, rows, endIndex: i - 1 };
};

// Basic Markdown Renderer since we don't have react-markdown
const SimpleMarkdownRenderer: React.FC<{
  content: string;
  onExportCSV?: (data: any[]) => void;
  editableCalendar?: boolean;
  onCalendarUpdate?: (updatedContent: string) => void;
  taskId?: string;
  rowStatuses?: Record<string, string>;
  onRowStatusChange?: (article: string, status: 'pending' | 'approved') => void;
  relatedContent?: Record<string, RelatedSourceTask>;
}> = ({ content, onExportCSV, editableCalendar, onCalendarUpdate, taskId, rowStatuses, onRowStatusChange, relatedContent }) => {
  const [contentFilter, setContentFilter] = useState<'all' | 'articles' | 'social_media'>('all');

  if (!content) return null;

  const lines = content.split('\n');
  const elements: React.ReactElement[] = [];
  let inList = false; // To track if we are inside a list block
  let i = 0;

  const renderText = (text: string, keyPrefix: string = '') => {
    // Handle inline code: `code`
    text = text.replace(/`([^`]+)`/g, (match, code) => `<code key="${keyPrefix}code-${Math.random().toString(36).substring(7)}">${code}</code>`);
    
    // Handle bold: **text**
    text = text.replace(/\*\*(.*?)\*\*/g, (match, boldText) => `<strong key="${keyPrefix}strong-${Math.random().toString(36).substring(7)}">${boldText}</strong>`);
    
    // Handle italics: *text*
    text = text.replace(/\*(.*?)\*/g, (match, italicText) => `<em key="${keyPrefix}em-${Math.random().toString(36).substring(7)}">${italicText}</em>`);

    // Handle links: [text](url)
    text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
      // Check if this is an internal preview link
      const isPreviewLink = url.startsWith('/preview/');
      if (isPreviewLink) {
        // For preview links, use a data attribute so we can handle click in React
        return `<a key="${keyPrefix}a-${Math.random().toString(36).substring(7)}" href="${url}" class="preview-link" data-preview-id="${url.replace('/preview/', '')}">${linkText}</a>`;
      }
      // External links open in new tab
      return `<a key="${keyPrefix}a-${Math.random().toString(36).substring(7)}" href="${url}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
    });

    // dangerouslySetInnerHTML is required because we're inserting HTML strings from renderText
    return <span dangerouslySetInnerHTML={{ __html: text }} />;
  };

  // Check if this is a content calendar (has "Content Calendar" header and table)
  const isContentCalendar = content.includes('## Content Calendar') && content.includes('| Date | Time | Article |');

  while (i < lines.length) {
    const line = lines[i];
    const key = `line-${i}`;

    // Table detection - markdown tables start with | (handle empty lines between header and separator)
    if (line.trim().startsWith('|')) {
      // Look ahead for separator row, skipping empty lines
      let nextNonEmpty = i + 1;
      while (nextNonEmpty < lines.length && lines[nextNonEmpty]?.trim() === '') nextNonEmpty++;
      
      if (lines[nextNonEmpty]?.trim().startsWith('|') && lines[nextNonEmpty]?.includes('-')) {
        const tableData = parseMarkdownTable(lines, i);
        if (tableData) {
          const { headers, rows, endIndex } = tableData;
          
          // Check if this looks like a content calendar table
          const isCalendarTable = headers.some(h => h.toLowerCase().includes('date')) && 
                                 headers.some(h => h.toLowerCase().includes('article'));
          
          // Use editable calendar component when in edit mode
          if (isCalendarTable && editableCalendar && onCalendarUpdate) {
            const csvData = rows.map(row => {
              const obj: any = {};
              headers.forEach((h, idx) => {
                obj[h.toLowerCase().replace(/\s+/g, '_')] = row[idx] || '';
              });
              return obj;
            });
            
            elements.push(
              <EditableContentCalendar
                key={key}
                tableData={csvData}
                taskId={taskId}
                rowStatuses={rowStatuses}
                onRowStatusChange={onRowStatusChange}
                onSave={(updatedData) => {
                  // Rebuild markdown table from updated data
                  const updatedRows = updatedData.map((row: any) =>
                    headers.map(h => row[h.toLowerCase().replace(/\s+/g, '_')] || '').join(' | ')
                  );
                  const updatedTable = [
                    '| ' + headers.join(' | ') + ' |',
                    '|' + headers.map(() => '------').join('|') + '|',
                    ...updatedRows.map(row => '| ' + row + ' |')
                  ].join('\n');
                  onCalendarUpdate(updatedTable);
                }}
              />
            );
          } else {
            // Find the article column index (3rd column typically, header "Article")
            const articleColIdx = headers.findIndex(h => h.toLowerCase() === 'article');

            elements.push(
              <div key={key} className="table-container">
                {isCalendarTable && (
                  <div className="calendar-controls">
                    {relatedContent && (
                      <select
                        className="content-type-filter"
                        value={contentFilter}
                        onChange={e => setContentFilter(e.target.value as 'all' | 'articles' | 'social_media')}
                      >
                        <option value="all">All Content</option>
                        <option value="articles">Articles Only</option>
                        <option value="social_media">Social Media Only</option>
                      </select>
                    )}
                    {onExportCSV && (
                      <GlassButton
                        variant="secondary"
                        icon={<Download size={16} />}
                        onClick={() => {
                          const csvData = rows.map(row => {
                            const obj: any = {};
                            headers.forEach((h, idx) => {
                              obj[h.toLowerCase().replace(/\s+/g, '_')] = row[idx] || '';
                            });
                            return obj;
                          });
                          onExportCSV(csvData);
                        }}
                      >
                        Export CSV
                      </GlassButton>
                    )}
                  </div>
                )}
                <table className="markdown-table">
                  <thead>
                    <tr>
                      {isCalendarTable && onRowStatusChange && <th className="status-col-header">Status</th>}
                      {headers.map((h, idx) => <th key={idx}>{renderText(h, key)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.flatMap((row, rowIdx) => {
                      const rawArticleCell = articleColIdx >= 0 ? (row[articleColIdx] || '').trim() : '';
                      // Strip markdown link syntax [Title](url) → Title
                      const mdLinkMatch = rawArticleCell.match(/^\[(.+?)\]\(.*?\)$/);
                      const articleTitle = mdLinkMatch ? mdLinkMatch[1].trim() : rawArticleCell;
                      const sourceTask = relatedContent ? relatedContent[articleTitle] : undefined;
                      const socialTasks = sourceTask?.related.filter(r => r.content_type === 'social_media') ?? [];
                      const showArticleRow = contentFilter !== 'social_media';
                      const showSubRows = contentFilter !== 'articles' && socialTasks.length > 0;

                      // Status for this row — keyed by the raw article cell value (matches DB storage)
                      const rowStatus = (rowStatuses?.[rawArticleCell] ?? 'pending') as 'pending' | 'approved';
                      const isApproved = rowStatus === 'approved';

                      const platformColors: Record<string, string> = {
                        linkedin: 'badge-linkedin',
                        twitter: 'badge-twitter',
                        facebook: 'badge-facebook',
                        instagram: 'badge-instagram',
                      };

                      const result: React.ReactElement[] = [];

                      if (showArticleRow) {
                        result.push(
                          <tr key={`row-${rowIdx}`} className={isCalendarTable && onRowStatusChange && isApproved ? 'row-approved' : ''}>
                            {isCalendarTable && onRowStatusChange && (
                              <td className="status-cell">
                                <button
                                  className={`row-status-btn status-${rowStatus}`}
                                  onClick={() => onRowStatusChange(rawArticleCell, isApproved ? 'pending' : 'approved')}
                                  title={isApproved ? 'Click to mark as Pending' : 'Click to mark as Approved'}
                                  type="button"
                                >
                                  {isApproved ? '✓ Approved' : '⏳ Pending'}
                                </button>
                              </td>
                            )}
                            {row.map((cell, cellIdx) => (
                              <td key={cellIdx}>{renderText(cell, `${key}-${rowIdx}`)}</td>
                            ))}
                          </tr>
                        );
                      }

                      if (showSubRows) {
                        socialTasks.forEach((social, sIdx) => {
                          result.push(
                            <tr key={`sub-${rowIdx}-${sIdx}`} className="calendar-sub-row social-media-sub-row">
                              {isCalendarTable && onRowStatusChange && <td></td>}
                              <td className="sub-row-indent"></td>
                              <td></td>
                              <td>↳ Social Media Posts</td>
                              <td>
                                <div className="platform-badges">
                                  {social.platforms.map(p => (
                                    <span key={p} className={`platform-badge ${platformColors[p] ?? ''}`}>
                                      {p.charAt(0).toUpperCase() + p.slice(1)}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              {headers.length > 4 && <td>—</td>}
                              <td>
                                <button
                                  className="sub-row-link"
                                  title="View social media posts"
                                  aria-label="View social media posts"
                                  onClick={() => window.open(`/preview/${social.task_id}`, '_blank')}
                                >
                                  <ExternalLink size={14} />
                                </button>
                              </td>
                            </tr>
                          );
                        });
                      }

                      return result;
                    })}
                  </tbody>
                </table>
              </div>
            );
          }
          i = endIndex + 1;
          inList = false;
          continue;
        }
      }
    }

    // Headers
    if (line.startsWith('### ')) {
      elements.push(<h3 key={key}>{renderText(line.replace('### ', ''), key)}</h3>);
      inList = false;
    } else if (line.startsWith('## ')) {
      elements.push(<h2 key={key}>{renderText(line.replace('## ', ''), key)}</h2>);
      inList = false;
    } else if (line.startsWith('# ')) {
      elements.push(<h1 key={key}>{renderText(line.replace('# ', ''), key)}</h1>);
      inList = false;
    }
    // Ordered List
    else if (line.match(/^\d+\.\s/)) {
        if (!inList) { 
            elements.push(<ol key={`ol-${i}`}></ol>); 
            inList = true; 
        }
        const currentOl = elements[elements.length - 1] as React.ReactElement<any>;
        elements[elements.length - 1] = React.cloneElement(currentOl, {
            children: [...(currentOl.props.children || []), <li key={key}>{renderText(line.replace(/^\d+\.\s/, ''), key)}</li>]
        });
    }
    // Unordered List
    else if (line.startsWith('* ') || line.startsWith('- ')) {
        if (!inList) { 
            elements.push(<ul key={`ul-${i}`}></ul>); 
            inList = true; 
        }
        const currentUl = elements[elements.length - 1] as React.ReactElement<any>;
        elements[elements.length - 1] = React.cloneElement(currentUl, {
            children: [...(currentUl.props.children || []), <li key={key}>{renderText(line.substring(2), key)}</li>]
        });
    }
    // Horizontal Rule
    else if (line.match(/^-{3,}|^\*{3,}|^_{3,}$/)) {
        elements.push(<hr key={key} />);
        inList = false;
    }
    // Empty line
    else if (line.trim() === '') {
        inList = false;
    }
    // Skip separator lines
    else if (line.match(/^\|[-:|\s]+\|$/)) {
        // Table separator, skip
    }
    // Paragraphs
    else {
        elements.push(<p key={key}>{renderText(line, key)}</p>);
        inList = false;
    }
    
    i++;
  }

  // Handle clicks on preview links
  const handleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'A' && target.classList.contains('preview-link')) {
      e.preventDefault();
      const previewId = target.getAttribute('data-preview-id');
      if (previewId) {
        // Open preview in a new tab
        window.open(`/preview/${previewId}`, '_blank');
      }
    }
  };

  return <div className="markdown-content" onClick={handleClick}>{elements}</div>;
};

// Icon-only copy button for a single metadata field (SEO Metadata quick win).
// Mirrors the schema-preview Copy/Copied! pattern below, but per-field and
// icon-only so it fits a section header's top-right corner.
const MetaFieldCopyButton: React.FC<{ text: string; label: string }> = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="meta-copy-btn"
      title={copied ? 'Copied!' : `Copy ${label}`}
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  );
};

const QuickWinRenderer: React.FC<{ data: any; type?: string; onPublishPlatform?: (platform: string) => void; articleImageUrl?: string; articleImageAlt?: string }> = ({ data, type, onPublishPlatform, articleImageUrl, articleImageAlt }) => {
  if (!data) return null;

  // Defensive: short-form types store raw markdown and should never reach this
  // renderer (renderContent handles them upstream), but if a stray dict ever
  // arrives, fall through to a markdown render of the wrapped text rather than
  // dumping JSON.
  if (type === 'faq_block' || type === 'content_block') {
    const text =
      (typeof data === 'string' && data) ||
      (typeof data?.text === 'string' && data.text) ||
      (typeof data?.response === 'string' && data.response) ||
      (typeof data?.result === 'string' && data.result) ||
      '';
    return (
      <div className={`quick-win-display shortform shortform-${type}`}>
        <SimpleMarkdownRenderer content={text} />
      </div>
    );
  }

  // Helper function to unescape newlines in text content
  const unescapeText = (text: string): string => {
    if (!text) return text;
    // Replace literal \n with actual newlines, and other common escape sequences
    return text.replace(/\\n/g, '\n').replace(/\\t/g, '\t').replace(/\\r/g, '');
  };

  const PlatformPublishBtn: React.FC<{ platform: string; label: string }> = ({ platform, label }) => {
    if (!onPublishPlatform) return null;
    return (
      <div className="platform-publish-row">
        <button className="platform-publish-btn" onClick={() => onPublishPlatform(platform)}>
          <Send size={13} /> Publish to {label}
        </button>
      </div>
    );
  };

  // Social Media Posts
  if (data.linkedin || data.twitter || data.facebook || data.instagram) {
    const hasArticleDetails = data.article_url || articleImageUrl;
    return (
      <div className="quick-win-display social-media">

        {/* Article Details panel — visible in read-only preview */}
        {hasArticleDetails && (
          <div className="preview-article-details">
            <h4 className="preview-article-details-title">Article Details</h4>
            {data.article_url && (
              <div className="preview-article-url-row">
                <span className="preview-article-url-label">URL:</span>
                <a href={data.article_url} target="_blank" rel="noopener noreferrer" className="preview-article-url-link">
                  {data.article_url}
                </a>
              </div>
            )}
            {articleImageUrl && (
              <div className="preview-article-image-row">
                <span className="preview-article-url-label">Image:</span>
                <img src={articleImageUrl} alt={articleImageAlt || 'Hero image'} className="preview-article-image-thumb" />
                <span className="preview-article-image-note">LinkedIn · Facebook · Instagram</span>
              </div>
            )}
          </div>
        )}

        {data.linkedin && (
          <section className="platform-section linkedin">
            <h3>LinkedIn</h3>
            {articleImageUrl && <img src={articleImageUrl} alt={articleImageAlt || ''} className="preview-platform-image-thumb" />}
            <div className="post-content">{unescapeText(
              typeof data.linkedin === 'string' ? data.linkedin : (data.linkedin.post || '')
            )}</div>
            {data.linkedin.hashtags && (
              <div className="hashtags">
                {data.linkedin.hashtags.map((tag: string) => (
                  <span key={tag} className="tag">#{tag}</span>
                ))}
              </div>
            )}
            <PlatformPublishBtn platform="linkedin" label="LinkedIn" />
          </section>
        )}

        {data.twitter && (
          <section className="platform-section twitter">
            <h3>Twitter / X</h3>
            {/* thread[] = old schema; post = new schema (single tweet) */}
            {data.twitter.thread ? (
              <div className="thread">
                {data.twitter.thread.map((tweet: string, i: number) => (
                  <div key={i} className="tweet">
                    <span className="tweet-number">{i + 1}</span>
                    <p>{unescapeText(tweet)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="post-content">{unescapeText(
                typeof data.twitter === 'string' ? data.twitter : (data.twitter.post || '')
              )}</div>
            )}
            {data.twitter.hashtags && (
              <div className="hashtags">
                {data.twitter.hashtags.map((tag: string) => (
                  <span key={tag} className="tag">#{tag}</span>
                ))}
              </div>
            )}
            <PlatformPublishBtn platform="twitter" label="X" />
          </section>
        )}

        {data.facebook && (
          <section className="platform-section facebook">
            <h3>Facebook</h3>
            {articleImageUrl && <img src={articleImageUrl} alt={articleImageAlt || ''} className="preview-platform-image-thumb" />}
            <div className="post-content">{unescapeText(
              typeof data.facebook === 'string' ? data.facebook : (data.facebook.post || '')
            )}</div>
            {data.facebook.cta && <div className="cta-box"><strong>CTA:</strong> {unescapeText(data.facebook.cta)}</div>}
            <PlatformPublishBtn platform="facebook" label="Facebook" />
          </section>
        )}

        {data.instagram && (
          <section className="platform-section instagram">
            <h3>Instagram</h3>
            {articleImageUrl && <img src={articleImageUrl} alt={articleImageAlt || ''} className="preview-platform-image-thumb" />}
            {/* caption = old schema; post = new schema */}
            <div className="post-content">{unescapeText(
              typeof data.instagram === 'string' ? data.instagram : (data.instagram.caption || data.instagram.post || '')
            )}</div>
            {data.instagram.hashtags && (
              <div className="hashtags">
                {data.instagram.hashtags.map((tag: string) => (
                  <span key={tag} className="tag">#{tag}</span>
                ))}
              </div>
            )}
            <PlatformPublishBtn platform="instagram" label="Instagram" />
          </section>
        )}
      </div>
    );
  }

  // SEO Metadata
  if (data.seo_title || data.meta_title || data.meta_description || data.focus_keyphrase) {
    // Normalise field names — AI returns seo_title; older records may use meta_title
    const seoTitle = data.seo_title || data.meta_title || '';
    const metaDesc = data.meta_description || data.description || '';
    const keyphrase = data.focus_keyphrase || data.focus_keyword || '';
    const slug = data.slug || '';
    const socialTitle = data.social_title || '';
    const socialDesc = data.social_description || '';
    const notes = data.notes || '';
    // Legacy keyword arrays
    const keywords: string[] = Array.isArray(data.keywords) ? data.keywords : [];
    // Backend auto-validation notes (additive — absent on legacy records)
    const validationNotes: string[] = Array.isArray(data.validation_notes) ? data.validation_notes : [];

    return (
      <div className="quick-win-display metadata">
        {seoTitle && (
          <section className="meta-section">
            <div className="meta-section-header">
              <h3>SEO Title</h3>
              <MetaFieldCopyButton text={seoTitle} label="SEO Title" />
            </div>
            <div className="meta-value">{seoTitle}</div>
            <div className="char-count">{seoTitle.length} chars</div>
          </section>
        )}
        {metaDesc && (
          <section className="meta-section">
            <div className="meta-section-header">
              <h3>Meta Description</h3>
              <MetaFieldCopyButton text={metaDesc} label="Meta Description" />
            </div>
            <div className="meta-value">{metaDesc}</div>
            <div className="char-count">{metaDesc.length} chars</div>
          </section>
        )}
        {keyphrase && (
          <section className="meta-section">
            <div className="meta-section-header">
              <h3>Focus Keyphrase</h3>
              <MetaFieldCopyButton text={keyphrase} label="Focus Keyphrase" />
            </div>
            <div className="meta-value">{keyphrase}</div>
          </section>
        )}
        {slug && (
          <section className="meta-section">
            <div className="meta-section-header">
              <h3>URL Slug</h3>
              <MetaFieldCopyButton text={slug} label="URL Slug" />
            </div>
            <code className="slug-value">{slug}</code>
          </section>
        )}
        {socialTitle && (
          <section className="meta-section">
            <div className="meta-section-header">
              <h3>Social Title</h3>
              <MetaFieldCopyButton text={socialTitle} label="Social Title" />
            </div>
            <div className="meta-value">{socialTitle}</div>
            <div className="char-count">{socialTitle.length} chars</div>
          </section>
        )}
        {socialDesc && (
          <section className="meta-section">
            <div className="meta-section-header">
              <h3>Social Description</h3>
              <MetaFieldCopyButton text={socialDesc} label="Social Description" />
            </div>
            <div className="meta-value">{socialDesc}</div>
            <div className="char-count">{socialDesc.length} chars</div>
          </section>
        )}
        {keywords.length > 0 && (
          <section className="meta-section">
            <div className="meta-section-header">
              <h3>Keywords</h3>
              <MetaFieldCopyButton text={keywords.join(', ')} label="Keywords" />
            </div>
            <div className="keywords-list">
              {keywords.map((kw: string) => (
                <span key={kw} className="keyword-tag">{kw}</span>
              ))}
            </div>
          </section>
        )}
        {notes && (
          <section className="meta-section">
            <div className="meta-section-header">
              <h3>Notes</h3>
              <MetaFieldCopyButton text={notes} label="Notes" />
            </div>
            <div className="meta-value">{notes}</div>
          </section>
        )}
        {validationNotes.length > 0 && (
          <section className="meta-section meta-validation">
            <h3>Auto-validation</h3>
            <ul className="meta-validation-list">
              {validationNotes.map((note: string, i: number) => (
                <li key={i} className="meta-validation-item">
                  <CheckCircle2 size={16} />
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    );
  }

  // Ad Copy
  if (data.google || data.facebook || data.linkedin) {
    return (
      <div className="quick-win-display ad-copy">
        {Object.entries(data).map(([platform, variations]: [string, any]) => (
          <section key={platform} className="platform-section">
            <h3>{platform.charAt(0).toUpperCase() + platform.slice(1)} Ads</h3>
            {Array.isArray(variations) ? (
              variations.map((variant: string, i: number) => (
                <div key={i} className="ad-variant">
                  <h4>Variation {i + 1}</h4>
                  <div className="ad-content">{variant}</div>
                </div>
              ))
            ) : (
              <div className="ad-variant">
                {Object.entries(variations).map(([key, val]) => (
                  <div key={key} className="ad-field">
                    <strong>{key}:</strong> {String(val)}
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}
      </div>
    );
  }

  // Content Calendar
  if (data.calendar && Array.isArray(data.calendar)) {
    // Calculate total entries
    const totalEntries = data.calendar.length;
    
    // Export calendar as CSV
    const exportCalendarCSV = () => {
      const headers = ['Date', 'Time', 'Theme', 'Content/Article', 'Platforms/Channels'];
      const rows = data.calendar.map((entry: any) => [
        entry.date || '',
        entry.time || '',
        entry.theme || '',
        entry.article_title || entry.topic || '',
        (entry.platforms || []).join(', ')
      ]);
      
      const csv = [headers.join(','), ...rows.map((r: string[]) => r.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))].join('\n');
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `content_calendar_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    return (
      <div className="quick-win-display calendar">
        <div className="calendar-header">
          <h3>Content Calendar</h3>
          <div className="calendar-actions">
            <span className="entry-count">{totalEntries} entries</span>
            <GlassButton variant="secondary" icon={<Download size={16} />} onClick={exportCalendarCSV}>
              Export CSV
            </GlassButton>
          </div>
        </div>
        <div className="calendar-grid">
          <table className="calendar-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Theme/Topic</th>
                <th>Content</th>
                <th>Channels</th>
              </tr>
            </thead>
            <tbody>
              {data.calendar.map((entry: any, i: number) => (
                <tr key={i}>
                  <td>
                    <div className="calendar-date">{entry.date}</div>
                    <div className="calendar-time">{entry.time}</div>
                  </td>
                  <td>{entry.theme}</td>
                  <td>{entry.article_title || entry.topic}</td>
                  <td>
                    <div className="channel-tags">
                      {(entry.platforms || []).map((p: string) => (
                        <span key={p} className="channel-tag">{p}</span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.strategy_notes && (
          <div className="strategy-notes">
            <h4>Strategy Notes</h4>
            <p>{data.strategy_notes}</p>
          </div>
        )}
      </div>
    );
  }

  // Email Newsletter
  if (data.subject_line || data.body_html || data.body_plain) {
    const altSubs: string[] = Array.isArray(data.alternative_subjects) ? data.alternative_subjects : [];
    return (
      <div className="quick-win-display newsletter">

        {/* ── Header metadata ───────────────────────────────────── */}
        <section className="newsletter-header-meta">
          {data.subject_line && (
            <div className="meta-section">
              <h3>Subject Line</h3>
              <div className="meta-value">{data.subject_line}</div>
              <div className="char-count">{data.subject_line.length} chars</div>
            </div>
          )}
          {data.preview_text && (
            <div className="meta-section">
              <h3>Preview Text</h3>
              <div className="meta-value">{data.preview_text}</div>
              <div className="char-count">{data.preview_text.length} chars</div>
            </div>
          )}
          {data.recommended_send_time && (
            <div className="meta-section">
              <h3>Recommended Send Time</h3>
              <div className="meta-value">{data.recommended_send_time}</div>
            </div>
          )}
          {data.audience_segment && (
            <div className="meta-section">
              <h3>Audience Segment</h3>
              <div className="meta-value">{data.audience_segment}</div>
            </div>
          )}
        </section>

        {/* ── HTML body preview ─────────────────────────────────── */}
        {data.body_html && (
          <section className="newsletter-preview">
            <h3>Email Preview (HTML)</h3>
            <div
              className="html-preview"
              dangerouslySetInnerHTML={{ __html: unescapeText(data.body_html) }}
            />
          </section>
        )}

        {/* ── Plain text body ───────────────────────────────────── */}
        {data.body_plain && (
          <section className="meta-section newsletter-plain">
            <h3>Plain Text Version</h3>
            <pre className="plain-text-body">{unescapeText(data.body_plain)}</pre>
          </section>
        )}

        {/* ── Alternative subjects ──────────────────────────────── */}
        {altSubs.length > 0 && (
          <section className="meta-section">
            <h3>Alternative Subject Lines</h3>
            <ul className="alternatives-list">
              {altSubs.map((sub: string, i: number) => (
                <li key={i}>{sub}</li>
              ))}
            </ul>
          </section>
        )}
      </div>
    );
  }

  // Carousel (multichannel_carousel)
  if (data.slides && Array.isArray(data.slides)) {
    return (
      <div className="quick-win-display carousel">
        {data.carousel_title && <h3 className="carousel-title">{data.carousel_title}</h3>}
        {data.theme && <p className="carousel-theme">{unescapeText(data.theme)}</p>}
        <div className="carousel-slides-grid">
          {data.slides.map((slide: any, i: number) => (
            <div key={i} className="carousel-slide-card">
              {slide.image_url && (
                <div className="carousel-slide-image">
                  <img src={slide.image_url} alt={slide.headline || `Slide ${slide.slide_number || i + 1}`} />
                </div>
              )}
              <div className="carousel-slide-content">
                {slide.slide_number != null && (
                  <span className="carousel-slide-number">Slide {slide.slide_number}</span>
                )}
                {slide.headline && <h4>{unescapeText(slide.headline)}</h4>}
                {slide.body && <p>{unescapeText(slide.body)}</p>}
                {slide.cta && <div className="carousel-slide-cta">{unescapeText(slide.cta)}</div>}
              </div>
            </div>
          ))}
        </div>
        {data.hashtags && Array.isArray(data.hashtags) && data.hashtags.length > 0 && (
          <div className="hashtags" style={{ marginTop: 12 }}>
            {data.hashtags.map((tag: string) => (
              <span key={tag} className="tag">#{tag}</span>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Fallback for unknown JSON
  return (
    <div className="json-content">
      <pre>{JSON.stringify(data, null, 2)}</pre>
    </div>
  );
};

const ContentPreview: React.FC<ContentPreviewProps> = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const { isLoading: authLoading } = useAuth();
  const [task, setTask] = useState<any>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [editorMode, setEditorMode] = useState<'wysiwyg' | 'raw'>('wysiwyg');
  const [relatedContent, setRelatedContent] = useState<Record<string, RelatedSourceTask> | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isResyncing, setIsResyncing] = useState(false);
  const [resyncPreview, setResyncPreview] = useState<ResyncPreview | null>(null);
  const [showResyncModal, setShowResyncModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regenerateFeedback, setRegenerateFeedback] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [userRating, setUserRating] = useState<1 | -1 | null>(null);
  const [approvalStatus, setApprovalStatus] = useState<string | null>(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishPlatformFilter, setPublishPlatformFilter] = useState<string | undefined>(undefined);
  const [taskExternalPostUrl, setTaskExternalPostUrl] = useState<string | null>(null);
  useEffect(() => {
    setTaskExternalPostUrl(task?.external_post_url ?? null);
  }, [task?.external_post_url]);
  const [repurposing, setRepurposing] = useState(false);
  const [repurposeResult, setRepurposeResult] = useState<RepurposeArticleResponse | null>(null);
  // Schedule-publication modal (reused from the catalogue) — articles only
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  // SEO metadata quick win — backend dedupes, returning the existing quick win
  // task when the article already has one, so this is double-click-safe.
  const [generatingSeo, setGeneratingSeo] = useState(false);
  const [seoTaskId, setSeoTaskId] = useState<string | null>(null);
  // Schema disclosure panel state
  const [schemaExpanded, setSchemaExpanded] = useState(false);
  const [schemaPreview, setSchemaPreview] = useState<SchemaPreviewResponse | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [schemaCopied, setSchemaCopied] = useState(false);
  const [showImageMenu, setShowImageMenu] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [showAiPrompt, setShowAiPrompt] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiImageError, setAiImageError] = useState('');
  const [cascadeDialogOpen, setCascadeDialogOpen] = useState(false);
  const [pendingCascadeData, setPendingCascadeData] = useState<{
    url: string;
    photographer: string;
    source: string;
  } | null>(null);
  const [isCascading, setIsCascading] = useState(false);
  const [cascadeError, setCascadeError] = useState<string | null>(null);
  const [pendingChildCount, setPendingChildCount] = useState(0);

  // IMPR-004: Escape key closes the cascade dialog (standard modal expectation).
  // Scoped to cascadeDialogOpen so the listener is only active while the dialog
  // is visible. Does nothing while a cascade PUT is in flight (isCascading).
  useEffect(() => {
    if (!cascadeDialogOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isCascading) {
        // handleCascadeDismiss is stable (no deps) — inline here to avoid
        // dependency-array complexity.
        setCascadeDialogOpen(false);
        setPendingCascadeData(null);
        setCascadeError(null);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [cascadeDialogOpen, isCascading]);
  const [excludedImageIds, setExcludedImageIds] = useState<number[]>([]);
  const [isFetchingImage, setIsFetchingImage] = useState(false);
  const [syncStatus, setSyncStatus] = useState<{
    total_articles: number;
    approved_articles: number;
    scheduled_publications: number;
    pending_articles: string[];
  } | null>(null);

  useEffect(() => {
    if (!taskId || authLoading) return;

    loadTask();
  }, [taskId, authLoading]);

  useEffect(() => {
    if (task?.input_data?.quick_win_type === 'content_calendar') {
      loadSyncStatus();
    }
  }, [task?.input_data?.quick_win_type, taskId]);

  // Lazy-fetch schema preview the first time the disclosure panel is expanded
  useEffect(() => {
    if (!schemaExpanded || !taskId || schemaPreview !== null || schemaLoading) return;
    const fetchSchema = async () => {
      setSchemaLoading(true);
      try {
        const data = await getSchemaPreview(taskId);
        setSchemaPreview(data);
      } catch {
        setSchemaPreview({ blocks: [], html: '', dropped: [], counts: { valid: 0, dropped: 0 } });
      } finally {
        setSchemaLoading(false);
      }
    };
    fetchSchema();
  }, [taskId, schemaExpanded, schemaPreview, schemaLoading]);

  const loadTask = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`/tasks/${taskId}`);
      setTask(response.data);
      setApprovalStatus(response.data.approval_status || null);
      setEditedContent(editSourceOf(response.data));

      // Fetch related content for content calendar tasks
      if (response.data.input_data?.quick_win_type === 'content_calendar') {
        try {
          const relatedResponse = await apiClient.get(`/tasks/${taskId}/related-content`);
          const sourceTasks: RelatedSourceTask[] = relatedResponse.data.source_tasks || [];
          const map: Record<string, RelatedSourceTask> = {};
          sourceTasks.forEach(st => {
            map[st.normalized_title] = st;
          });
          setRelatedContent(map);
        } catch (relErr) {
          console.warn('Could not load related content:', relErr);
        }
      }
    } catch (err: any) {
      console.error('Failed to load task:', err);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async (withResearch: boolean = false) => {
    if (!taskId) return;
    setIsRegenerating(true);
    try {
      const res = await apiClient.post(`/tasks/${taskId}/regenerate`, {
        feedback: regenerateFeedback || null,
        // Force live research on the regenerated task (used by the
        // "Regenerate with Research" button when auto-mode skipped grounding)
        grounding_mode: withResearch ? 'gemini_search' : null,
      });
      const newTaskId = res.data.task_id;
      setShowRegenerateModal(false);
      setRegenerateFeedback('');
      // Navigate to the new task
      window.location.href = `/preview/${newTaskId}`;
    } catch (err) {
      console.error('Regenerate failed:', err);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleExport = async (format: 'html' | 'docx') => {
    if (!taskId) return;
    try {
      // Use apiClient so the Authorization header is included automatically
      const response = await apiClient.get(`/tasks/${taskId}/export`, {
        params: { format },
        responseType: 'blob',
      });
      const mime = format === 'docx'
        ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        : 'text/html';
      const url = URL.createObjectURL(new Blob([response.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${taskId}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const handleRating = async (rating: 1 | -1) => {
    if (!taskId) return;
    setUserRating(rating);
    try {
      await apiClient.post(`/tasks/${taskId}/rating`, { rating });
    } catch { /* non-fatal */ }
  };

  // Derive calendar row statuses from each linked article's approval_status.
  // Keyed by the markdown link string the calendar table uses (e.g.
  // "[Title](/preview/<task_id>)") to match the renderer's existing lookup.
  const articleApprovalRowStatuses = React.useMemo<Record<string, 'pending' | 'approved'>>(() => {
    if (!task?.synthesized_result || !relatedContent) return {};
    const approvalByTaskId: Record<string, string | null | undefined> = {};
    Object.values(relatedContent).forEach(src => {
      approvalByTaskId[src.task_id] = src.approval_status ?? null;
    });
    const map: Record<string, 'pending' | 'approved'> = {};
    const linkRegex = /\[[^\]]+\]\(\/preview\/([0-9a-f-]{36})\)/g;
    const seen = new Set<string>();
    let match: RegExpExecArray | null;
    while ((match = linkRegex.exec(task.synthesized_result)) !== null) {
      const fullLink = match[0];
      const articleTaskId = match[1];
      if (seen.has(fullLink)) continue;
      seen.add(fullLink);
      map[fullLink] = approvalByTaskId[articleTaskId] === 'approved' ? 'approved' : 'pending';
    }
    return map;
  }, [task?.synthesized_result, relatedContent]);

  const handleRowStatusChange = async (article: string, status: 'pending' | 'approved') => {
    if (!taskId || !task) return;
    // Parse the linked article's task_id out of the markdown-link cell value.
    const m = article.match(/\/preview\/([0-9a-f-]{36})/);
    if (!m) {
      console.warn('Calendar row has no preview link — cannot sync approval', article);
      return;
    }
    const articleTaskId = m[1];
    try {
      // Single source of truth: write to the article task's approval_status.
      await updateTaskApproval(articleTaskId, status === 'approved' ? 'approved' : null);
      // Refresh related-content so the derived row map updates.
      const relatedResponse = await apiClient.get(`/tasks/${taskId}/related-content`);
      const sourceTasks: RelatedSourceTask[] = relatedResponse.data.source_tasks || [];
      const map: Record<string, RelatedSourceTask> = {};
      sourceTasks.forEach(st => {
        map[st.normalized_title] = st;
      });
      setRelatedContent(map);
      if (task.input_data?.quick_win_type === 'content_calendar') {
        loadSyncStatus();
      }
    } catch (err) {
      console.error('Failed to save row status:', err);
    }
  };

  const loadSyncStatus = async () => {
    if (!taskId) return;
    try {
      const status = await getContentCalendarSyncStatus(taskId);
      setSyncStatus(status);
    } catch (err) {
      console.error('Failed to load sync status:', err);
    }
  };

  const handleSyncToScheduler = async () => {
    if (!taskId) return;

    const confirmSync = window.confirm(
      `This will create scheduled publications for all approved articles in this calendar.\n\n` +
      `Approved articles: ${syncStatus?.approved_articles || 0}\n` +
      `Already scheduled: ${syncStatus?.scheduled_publications || 0}\n` +
      `Pending: ${syncStatus?.pending_articles?.length || 0}\n\n` +
      `Continue?`
    );

    if (!confirmSync) return;

    setIsSyncing(true);
    try {
      const result = await syncContentCalendar(taskId);

      if (result.success) {
        // Separate warnings from actual errors
        const warnings = result.errors.filter((e: string) => e.startsWith('⚠️'));
        const actualErrors = result.errors.filter((e: string) => !e.startsWith('⚠️'));

        let message = `✓ Sync completed!\n\n` +
          `Created: ${result.created} publication(s)\n` +
          `Skipped: ${result.skipped}`;

        if (warnings.length > 0) {
          message += `\n\n⚠️ Warnings:\n${warnings.join('\n\n')}`;
        }

        if (actualErrors.length > 0) {
          message += `\n\n❌ Errors:\n${actualErrors.join('\n')}`;
        }

        alert(message);

        // Reload sync status
        await loadSyncStatus();
      } else {
        alert(`Sync failed:\n${result.errors.join('\n')}`);
      }
    } catch (err: any) {
      console.error('Failed to sync calendar:', err);
      alert('Failed to sync calendar: ' + getApiError(err, err.message));
    } finally {
      setIsSyncing(false);
    }
  };

  const handleOpenResync = async () => {
    if (!taskId) return;
    setIsResyncing(true);
    try {
      const preview = await previewCalendarResync(taskId);
      setResyncPreview(preview);
      setShowResyncModal(true);
    } catch (err: any) {
      alert('Failed to load re-sync preview: ' + getApiError(err, err.message));
    } finally {
      setIsResyncing(false);
    }
  };

  const handleApplyResync = async () => {
    if (!taskId) return;
    setIsResyncing(true);
    try {
      const result = await applyCalendarResync(taskId);
      setShowResyncModal(false);
      setResyncPreview(null);

      if (result.success) {
        let msg = `✓ Re-sync complete!\n\nUpdated: ${result.updated}\nCancelled: ${result.cancelled}\nAdded: ${result.added}\nUnchanged: ${result.unchanged}`;
        if (result.errors.length > 0) {
          msg += `\n\n⚠️ Warnings:\n${result.errors.join('\n')}`;
        }
        alert(msg);
        await loadSyncStatus();
      } else {
        alert(`Re-sync failed:\n${result.errors.join('\n')}`);
      }
    } catch (err: any) {
      alert('Re-sync failed: ' + getApiError(err, err.message));
    } finally {
      setIsResyncing(false);
    }
  };

  // Single save chokepoint for every editor branch (article/social/newsletter/
  // calendar). Replaces four near-identical inline persistence bodies so the
  // save-time wrapper guard and the dual local-state update (both
  // synthesized_result AND synthesized_result_clean) can't be forgotten at any
  // one call site.
  const persistSynthesizedResult = async (next: string, successMessage: string): Promise<boolean> => {
    if (looksLikeWrappedGeneratorOutput(next)) {
      console.error('[ContentPreview] refused to save wrapper-shaped content', {
        taskId, prefix: next.slice(0, 80),
      });
      alert(
        "This content still looks like raw generator output (a {'response': …} wrapper) " +
        "rather than the article body. Nothing has been saved. Please reload the page and " +
        "try again — if it keeps happening, report this task ID: " + taskId
      );
      return false;
    }
    setIsSaving(true);
    try {
      await updateTask(taskId!, { synthesized_result: next });
      // Both fields must be updated together: if only synthesized_result were
      // set, a later Cancel (which reads editSourceOf(task)) would read the
      // now-stale synthesized_result_clean from the pre-edit wrapper and
      // silently revert the editor to the old text.
      setTask((prev: any) => ({ ...prev, synthesized_result: next, synthesized_result_clean: next }));
      setEditedContent(next);
      setIsEditing(false);
      alert(successMessage);
      return true;
    } catch (err: any) {
      console.error('Failed to save:', err);
      alert('Failed to save changes. Please try again.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const renderContent = () => {
    if (!task || !task.synthesized_result) {
      return <p>No content available</p>;
    }

    // Prefer the backend-unwrapped field; the entire heuristic below remains
    // as the fallback for a task fetched without it (list endpoints, an older
    // cached response). Strictly narrowing: this can only make the already-
    // working read path healthier, never worse (Phase 3).
    let content = editSourceOf(task);

    // editSourceOf() can return an already-unwrapped value that is itself a
    // top-level ```json/```markdown fenced blob — the wrapper's inner value
    // was fenced (41 social_media_posts + 27 meta_data rows, plus 1 fenced
    // markdown article). The legacy heuristic's fence strip below only runs
    // inside the Python-dict-repr branch, which is unreachable once the
    // wrapper has already been removed, so strip a top-level fence here
    // first — this is what makes Phase 3 an actual no-op for those rows.
    if (typeof content === 'string') {
      // The info-string group is matched and discarded explicitly (any
      // word-ish tag, not just json/markdown) so an unrecognized language
      // (e.g. ```html) can't leak its tag into the captured content.
      const fenced = content.trim().match(/^```[a-zA-Z0-9_-]*\n([\s\S]*?)\n?```$/);
      if (fenced) {
        content = fenced[1];
      }
    }

    let isObject = false;
    let parsedObj = null;

    // Try to parse JSON (Standard double quotes)
    try {
      const parsed = typeof content === 'string' ? JSON.parse(content) : content;
      if (typeof parsed === 'object' && parsed !== null) {
        isObject = true;
        parsedObj = parsed;
      }
    } catch (e) {
      // JSON parse failed. Check for Python-style dict string (single quotes)
      const trimmedContent = typeof content === 'string' ? content.trim() : '';

      // Check for different Python dict formats
      let contentKey = '';
      let endMarker = '';

      if (trimmedContent.startsWith("{'response':")) {
        contentKey = "'response': '";
        endMarker = ", 'stats':";
        console.log("Detected Python dict format with 'response' key");
      } else if (trimmedContent.startsWith("{'type':")) {
        contentKey = "'result': '";
        endMarker = "', 'session_id':";  // result field is followed by session_id
        console.log("Detected Python dict format with 'result' key");
      }

      if (contentKey && typeof content === 'string') {
        console.log("Attempting Python dict extraction...");
        // Extract from Python dict string format
        try {
            let jsonStr = content.trim();

            // Find the start of the content value
            const contentStart = jsonStr.indexOf(contentKey) + contentKey.length;

            // Find the end marker
            let contentEnd = jsonStr.lastIndexOf(endMarker);

            // If end marker not found, look for the last ', before the final }
            if (contentEnd === -1) {
                // Find all occurrences of ', and take the one before the last }
                const lastBrace = jsonStr.lastIndexOf('}');
                contentEnd = jsonStr.lastIndexOf("',", lastBrace);
                if (contentEnd === -1) contentEnd = lastBrace - 1;
            }

            if (contentStart > 0 && contentEnd > contentStart) {
                // Extract the content (still has the trailing quote)
                let responseContent = jsonStr.substring(contentStart, contentEnd);

                // Remove the trailing quote
                if (responseContent.endsWith("'")) {
                    responseContent = responseContent.slice(0, -1);
                }

                console.log("Extracted content length:", responseContent.length);

                // CRITICAL: Unescape Python string encoding
                // Replicates Python's .encode().decode('unicode_escape') where:
                // - \n (2 chars) becomes actual newline (1 char)
                // - \\n (3 chars) becomes \n (2 chars, which is JSON escape sequence)

                // Use a function that processes escape sequences like Python's unicode_escape
                const unescapePythonString = (str: string): string => {
                    return str.replace(/\\(.)/g, (match, char) => {
                        switch (char) {
                            case 'n': return '\n';
                            case 't': return '\t';
                            case 'r': return '\r';
                            case '\\': return '\\';
                            case "'": return "'";
                            case '"': return '"';
                            default: return match; // Keep unknown escapes as-is
                        }
                    });
                };

                responseContent = unescapePythonString(responseContent);

                console.log("After unescaping, first 100 chars:", responseContent.substring(0, 100));

                // Now remove markdown code fences (which now have actual newlines)
                if (responseContent.startsWith('```json\n')) {
                    responseContent = responseContent.substring(8); // Remove '```json\n'
                }
                if (responseContent.endsWith('\n```')) {
                    responseContent = responseContent.slice(0, -4); // Remove '\n```'
                } else if (responseContent.endsWith('```')) {
                    responseContent = responseContent.slice(0, -3); // Remove '```'
                }

                console.log("After removing code fences:", responseContent.substring(0, 100));

                // Check if content looks like markdown (starts with # header) or plain text
                // If so, treat it as plain text content, not JSON
                const looksLikeMarkdown = responseContent.trim().startsWith('#') || 
                                         responseContent.trim().startsWith('##') ||
                                         responseContent.includes('\n# ') ||
                                         responseContent.trim().startsWith('Estate') || // Common article start
                                         !responseContent.trim().startsWith('{'); // Anything not JSON
                
                if (looksLikeMarkdown) {
                    console.log("Content appears to be markdown/plain text, not JSON. Using as-is.");
                    content = responseContent;
                } else {
                    // Now try to parse as JSON.
                    // Attempt 1: clean parse (preserves all escape sequences inside JSON strings)
                    let jsonParsed = false;
                    try {
                        const parsed = JSON.parse(responseContent);
                        if (typeof parsed === 'object' && parsed !== null) {
                            isObject = true;
                            parsedObj = parsed;
                            jsonParsed = true;
                        }
                    } catch (_) {}

                    // Attempt 2: strip only real non-printable control chars (not \t \n \r)
                    if (!jsonParsed) {
                        try {
                            const sanitized = responseContent
                                .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/g, '');
                            const parsed = JSON.parse(sanitized);
                            if (typeof parsed === 'object' && parsed !== null) {
                                isObject = true;
                                parsedObj = parsed;
                                jsonParsed = true;
                            }
                        } catch (parseErr) {
                            content = responseContent; // Fallback to extracted string
                        }
                    }
                }
            } else {
                console.error("Could not find response boundaries");
            }
        } catch (err) {
            console.error("Failed to extract python dict string", err);
        }
      }
    }

    const quickWinType = task.input_data?.quick_win_type;
    const isShortformQuickWin = quickWinType === 'faq_block' || quickWinType === 'content_block';

    // CASE 1: Quick Wins
    if (quickWinType) {
      // Short-form types store raw markdown — render via SimpleMarkdownRenderer
      // regardless of whether the upstream JSON-parse path happened to succeed.
      if (isShortformQuickWin) {
        const contentStr = typeof content === 'string'
          ? content
          : (typeof task.synthesized_result === 'string' ? task.synthesized_result : '');
        return (
          <div className={`quick-win-display shortform shortform-${quickWinType}`}>
            <SimpleMarkdownRenderer content={contentStr} />
          </div>
        );
      }
      if (isObject && parsedObj) {
        if (parsedObj.response && typeof parsedObj.response === 'object') {
            parsedObj = parsedObj.response;
        } else if (parsedObj.response && typeof parsedObj.response === 'string') {
            try { parsedObj = JSON.parse(parsedObj.response); } catch(e) {}
        }
        return <QuickWinRenderer
          data={parsedObj}
          type={quickWinType}
          articleImageUrl={task?.output_data?.images?.hero?.urls?.large2x || task?.output_data?.images?.hero?.urls?.original}
          articleImageAlt={task?.output_data?.images?.hero?.description}
          onPublishPlatform={task?.status === 'completed' ? (platform: string) => {
            setPublishPlatformFilter(platform);
            setShowPublishModal(true);
          } : undefined}
        />;
      }
      
      // If content is markdown (starts with ## or contains markdown table), render with SimpleMarkdownRenderer
      const contentStr = typeof content === 'string' ? content : '';
      if (contentStr.includes('## Content Calendar') || contentStr.includes('| Date | Time |')) {
        // CSV export handler for content calendar tables
        const handleExportCSV = (data: any[]) => {
          if (!data || data.length === 0) return;
          const headers = Object.keys(data[0]);
          const csvRows = [
            headers.join(','),
            ...data.map(row => 
              headers.map(h => {
                const val = row[h] || '';
                if (typeof val === 'string' && (val.includes(',') || val.includes('"') || val.includes('\n'))) {
                  return `"${val.replace(/"/g, '""')}"`;
                }
                return val;
              }).join(',')
            )
          ];
          const csvContent = csvRows.join('\n');
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `content_calendar_${new Date().toISOString().split('T')[0]}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        };
        return <SimpleMarkdownRenderer content={contentStr} onExportCSV={handleExportCSV} relatedContent={relatedContent ?? undefined} rowStatuses={articleApprovalRowStatuses} onRowStatusChange={handleRowStatusChange} />;
      }
      
      return <div className="json-content"><pre>{content}</pre></div>;
    }

    // CASE 2: Articles (Markdown)
    if (isObject && parsedObj) {
      if (parsedObj.response && typeof parsedObj.response === 'string') {
        content = parsedObj.response;
      } else if (parsedObj.result && typeof parsedObj.result === 'string') {
        content = parsedObj.result;
      } else if (parsedObj.content && typeof parsedObj.content === 'string') {
        content = parsedObj.content;
      }
    }

    // Ensure content is a string
    if (typeof content !== 'string') {
        content = JSON.stringify(content, null, 2);
    }

    // CRITICAL FIX: Unescape newlines that may be stored as literal \n\n in the database
    // This handles cases where content has escaped newlines like "text\\n\\nmore text"
    content = content.replace(/\\n/g, '\n');

    // Also handle other common escape sequences
    content = content.replace(/\\t/g, '\t');
    content = content.replace(/\\r/g, '');

    // Clean up LLM meta-commentary (e.g., "I made a mistake... # Title")
    // If we find a markdown header "# ", treat everything before it as noise
    const headerIndex = content.indexOf('\n# ');
    if (headerIndex !== -1) {
        // Keep the header and everything after
        content = content.substring(headerIndex + 1); // +1 to include the newline's break but remove the text before
    } else if (content.startsWith('# ')) {
        // Perfect, starts with header
    } else {
        // Try finding header without newline (start of string)
        const startHeaderIndex = content.indexOf('# ');
        if (startHeaderIndex > 0) {
             content = content.substring(startHeaderIndex);
        }
    }

    // CSV export handler for content calendar tables
    const handleExportCSV = (data: any[]) => {
      if (!data || data.length === 0) return;

      // Get headers from first object
      const headers = Object.keys(data[0]);

      // Create CSV content
      const csvRows = [
        headers.join(','),
        ...data.map(row =>
          headers.map(h => {
            const val = row[h] || '';
            // Escape values with commas or quotes
            if (typeof val === 'string' && (val.includes(',') || val.includes('"') || val.includes('\n'))) {
              return `"${val.replace(/"/g, '""')}"`;
            }
            return val;
          }).join(',')
        )
      ];

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `content_calendar_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    return <SimpleMarkdownRenderer content={content} onExportCSV={handleExportCSV} relatedContent={relatedContent ?? undefined} rowStatuses={articleApprovalRowStatuses} onRowStatusChange={handleRowStatusChange} />;
  };

  const handleFetchNewImage = async () => {
    if (!taskId) return;
    setIsFetchingImage(true);
    setShowImageMenu(false);

    // Add current hero's pexels_id to exclusion list
    const currentId = task?.output_data?.images?.hero?.pexels_id;
    const newExcluded = currentId ? [...excludedImageIds, currentId] : excludedImageIds;
    setExcludedImageIds(newExcluded);

    try {
      const result = await selectImagesForTask(taskId, true, newExcluded);
      if (result.images) {
        setTask((prev: any) => ({
          ...prev,
          output_data: {
            ...prev.output_data,
            images: result.images,
          }
        }));
      }
      // Check for derived social posts to offer cascade (IMPR-002: pass count directly)
      const heroUrl =
        result.images?.hero?.urls?.large2x ||
        result.images?.hero?.urls?.large ||
        result.images?.hero?.urls?.original || '';
      if (heroUrl && taskId) {
        try {
          const childCount = await getChildSocialPostCount(taskId);
          maybePromptCascade(childCount, heroUrl, result.images?.hero?.photographer || '', 'pexels');
        } catch { /* non-fatal — cascade prompt is best-effort */ }
      }
    } catch (err: any) {
      alert(getApiError(err, 'Failed to fetch a new image.'));
    } finally {
      setIsFetchingImage(false);
    }
  };

  const handleUploadImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (!file || !taskId) return;

      setIsFetchingImage(true);
      setShowImageMenu(false);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const uploadRes = await apiClient.post(`/brand-guides/upload-asset`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const assetUrl = uploadRes.data.url || uploadRes.data.path;

        const result = await updateHeroImage(taskId, assetUrl, '', 'upload');
        setTask((prev: any) => ({
          ...prev,
          output_data: {
            ...prev.output_data,
            images: { ...prev.output_data?.images, hero: result.hero },
          }
        }));
        maybePromptCascade(result.child_social_post_count ?? 0, assetUrl, '', 'upload');
      } catch (err: any) {
        alert(getApiError(err, 'Failed to upload image.'));
      } finally {
        setIsFetchingImage(false);
      }
    };
    input.click();
  };

  const handleInsertUrl = async () => {
    if (!imageUrl.trim() || !taskId) return;
    setIsFetchingImage(true);
    setShowUrlInput(false);
    setShowImageMenu(false);
    try {
      const result = await updateHeroImage(taskId, imageUrl.trim(), '', 'url');
      setTask((prev: any) => ({
        ...prev,
        output_data: {
          ...prev.output_data,
          images: { ...prev.output_data?.images, hero: result.hero },
        }
      }));
      maybePromptCascade(result.child_social_post_count ?? 0, imageUrl.trim(), '', 'url');
      setImageUrl('');
    } catch (err: any) {
      alert(getApiError(err, 'Failed to set image URL.'));
    } finally {
      setIsFetchingImage(false);
    }
  };

  // Reuses isFetchingImage — only one image operation can run at a time.
  // The spec defined a separate isGeneratingAi state but the shared flag is
  // sufficient here and matches existing code style in this file.
  const handleGenerateAiImage = async () => {
    if (!aiPrompt.trim() || !taskId) return;
    setIsFetchingImage(true);
    setAiImageError('');
    try {
      const result = await generateAiHeroImage(aiPrompt.trim(), task?.name);
      const heroResult = await updateHeroImage(taskId, result.url, '', 'ai-generated');
      setTask((prev: any) => ({
        ...prev,
        output_data: {
          ...prev.output_data,
          images: { ...prev.output_data?.images, hero: heroResult.hero },
        }
      }));
      maybePromptCascade(heroResult.child_social_post_count ?? 0, result.url, '', 'ai-generated');
      // Prompt stays visible so user can re-generate with tweaks
    } catch (err: any) {
      setAiImageError(err?.response?.data?.detail || err?.message || 'Image generation failed.');
    } finally {
      setIsFetchingImage(false);
    }
  };

  // IMPR-002: accepts count directly rather than a HeroImageResponse object —
  // three callers already have the count on the response; handleFetchNewImage
  // passes it via getChildSocialPostCount. Passing a synthetic response object
  // was dead noise that implied the helper did more than it does.
  const maybePromptCascade = (
    childCount: number,
    url: string,
    photographer: string,
    source: string,
  ) => {
    if (childCount > 0) {
      setCascadeError(null);
      setPendingChildCount(childCount);
      setPendingCascadeData({ url, photographer, source });
      setCascadeDialogOpen(true);
    }
  };

  const handleCascadeConfirm = async () => {
    if (!pendingCascadeData || !taskId) return;
    setIsCascading(true);
    setCascadeError(null);
    try {
      await updateHeroImage(
        taskId,
        pendingCascadeData.url,
        pendingCascadeData.photographer,
        pendingCascadeData.source,
        { cascade: true },
      );
      setCascadeDialogOpen(false);
      setPendingCascadeData(null);
    } catch (err: any) {
      setCascadeError(err?.response?.data?.detail || err?.message || 'Cascade failed. Please try again.');
    } finally {
      setIsCascading(false);
    }
  };

  const handleCascadeDismiss = () => {
    setCascadeDialogOpen(false);
    setPendingCascadeData(null);
    setCascadeError(null);
  };

  const renderHeroImage = () => {
    if (!task?.output_data?.images?.hero) return null;

    const hero = task.output_data.images.hero;

    return (
      <div className="hero-image-container">
        <img
          src={hero.urls?.large2x || hero.urls?.original}
          alt={hero.description || 'Hero image'}
          className="hero-image"
        />
        {hero.photographer && hero.pexels_url && (
          <p className="image-attribution">
            Photo by <a href={hero.pexels_url} target="_blank" rel="noopener noreferrer">
              {hero.photographer}
            </a> on <a href="https://www.pexels.com" target="_blank" rel="noopener noreferrer">
              Pexels
            </a>
          </p>
        )}
        <div className="hero-image-actions">
          <button
            className="update-image-btn"
            onClick={() => { setShowImageMenu(!showImageMenu); setShowUrlInput(false); setShowAiPrompt(false); }}
            disabled={isFetchingImage}
          >
            {isFetchingImage ? <Loader2 size={14} className="spin" /> : <ImageIcon size={14} />}
            {isFetchingImage ? 'Loading...' : 'Update Image'}
          </button>
          {showImageMenu && (
            <div className="image-menu">
              <button onClick={handleFetchNewImage}>
                <RefreshCw size={14} /> Fetch New Image
              </button>
              <button onClick={handleUploadImage}>
                <Upload size={14} /> Upload Image
              </button>
              <button onClick={() => { setShowUrlInput(true); setShowAiPrompt(false); setShowImageMenu(false); }}>
                <Link size={14} /> Insert Image URL
              </button>
              <button onClick={() => {
                setShowAiPrompt(true);
                setShowUrlInput(false);
                setShowImageMenu(false);
                setAiImageError('');
                if (!aiPrompt) {
                  setAiPrompt(task?.output_data?.images?.hero?.description || task?.name || '');
                }
              }}>
                <Wand2 size={14} /> Generate Image
              </button>
            </div>
          )}
          {showUrlInput && (
            <div className="image-url-input">
              <input
                type="url"
                placeholder="https://example.com/image.jpg"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleInsertUrl(); }}
                autoFocus
              />
              <button onClick={handleInsertUrl} disabled={!imageUrl.trim()}>
                <Check size={14} />
              </button>
              <button onClick={() => { setShowUrlInput(false); setImageUrl(''); }}>
                <X size={14} />
              </button>
            </div>
          )}
          {showAiPrompt && (
            <div className="image-ai-prompt">
              <textarea
                className="image-ai-prompt-input"
                placeholder="Describe the image to generate…"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={3}
                autoFocus
              />
              {aiImageError && <p className="image-ai-error">{aiImageError}</p>}
              <div className="image-ai-prompt-actions">
                <button onClick={handleGenerateAiImage} disabled={isFetchingImage || !aiPrompt.trim()}>
                  {isFetchingImage ? <Loader2 size={14} className="spin" /> : <Wand2 size={14} />}
                  {isFetchingImage ? 'Generating…' : 'Generate'}
                </button>
                <button onClick={() => { setShowAiPrompt(false); setAiPrompt(''); setAiImageError(''); }}>
                  <X size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <GlassCard variant="default" padding="lg" className="content-preview loading" style={{ margin: '40px auto', maxWidth: '400px' }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: 'var(--text-secondary)' }}>Loading content...</p>
        </div>
      </GlassCard>
    );
  }

  if (error) {
    return (
      <GlassCard variant="default" padding="lg" className="content-preview error" style={{ margin: '40px auto', maxWidth: '400px' }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <h2 style={{ margin: '0 0 12px 0', color: 'var(--danger)' }}>Error</h2>
          <p style={{ color: 'var(--text-secondary)' }}>{error}</p>
        </div>
      </GlassCard>
    );
  }

  if (!task) {
    return (
      <GlassCard variant="default" padding="lg" className="content-preview empty" style={{ margin: '40px auto', maxWidth: '400px' }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: 'var(--text-secondary)' }}>Content not found</p>
        </div>
      </GlassCard>
    );
  }

  // If editing, render editor WITHOUT GlassCard wrapper to avoid overflow/containing block issues
  if (isEditing) {
    // Check if this is a content calendar for special editing
    const isCalendar = editedContent.includes('## Content Calendar') && 
                      editedContent.includes('| Date | Time | Article |');
    
    if (isCalendar) {
      // Parse the calendar table for editing
      const lines = editedContent.split('\n');
      const tableStart = lines.findIndex(l => l.includes('| Date | Time | Article |'));
      
      if (tableStart > 0) {
        const tableData = parseMarkdownTable(lines, tableStart);
        if (tableData) {
          const { headers, rows } = tableData;
          const csvData = rows.map(row => {
            const obj: any = {};
            headers.forEach((h, idx) => {
              obj[h.toLowerCase().replace(/\s+/g, '_')] = row[idx] || '';
            });
            return obj;
          });
          
          return (
            <div style={{ height: '100vh', overflow: 'auto', background: 'var(--background, #ffffff)' }}>
              <div className="preview-body edit-mode" style={{ maxWidth: '95%', margin: '0 auto', padding: '40px 20px' }}>
                <h2>Edit Content Calendar</h2>
                <EditableContentCalendar
                tableData={csvData}
                taskId={taskId}
                rowStatuses={articleApprovalRowStatuses}
                onRowStatusChange={handleRowStatusChange}
                onSave={async (updatedData) => {
                  // Rebuild markdown with updated table
                  const beforeTable = lines.slice(0, tableStart).join('\n');
                  const updatedRows = updatedData.map((row: any) =>
                    headers.map(h => row[h.toLowerCase().replace(/\s+/g, '_')] || '').join(' | ')
                  );
                  const updatedTable = [
                    '| ' + headers.join(' | ') + ' |',
                    '|' + headers.map(() => '------').join('|') + '|',
                    ...updatedRows.map(row => '| ' + row + ' |')
                  ].join('\n');
                  const afterTableIndex = tableStart;
                  let afterTable = '';
                  for (let i = afterTableIndex + rows.length + 2; i < lines.length; i++) {
                    if (lines[i] && !lines[i].startsWith('|')) {
                      afterTable = lines.slice(i).join('\n');
                      break;
                    }
                  }

                  const updatedContent = beforeTable + '\n' + updatedTable + '\n\n' + afterTable;

                  return await persistSynthesizedResult(updatedContent, 'Calendar saved successfully!');
                }}
              />
              <div style={{ marginTop: 'var(--space-4)' }}>
                <GlassButton
                  variant="danger"
                  onClick={() => {
                    setEditedContent(editSourceOf(task));
                    setIsEditing(false);
                  }}
                  icon={<XCircle size={16} />}
                >
                  Cancel Editing
                </GlassButton>
              </div>
              </div>
            </div>
          );
        }
      }
    }
    
    // Social media post editor
    if (task?.input_data?.quick_win_type === 'social_media_posts') {
      return (
        <div style={{ height: '100vh', overflow: 'auto', background: 'var(--background, #ffffff)' }}>
          <EditableSocialMediaViewer
            rawContent={editedContent}
            articleImageUrl={task?.output_data?.images?.hero?.urls?.large2x || task?.output_data?.images?.hero?.urls?.original}
            articleImageAlt={task?.output_data?.images?.hero?.description}
            taskId={taskId}
            onHeroImageChanged={async (_newUrl) => {
              // Re-fetch task so output_data.images.hero reflects the new URL,
              // keeping per-platform thumbnails in sync.
              try {
                const refreshed = await getTask(taskId!);
                setTask(refreshed);
              } catch {
                // Fallback: optimistically patch the URL so thumbnails update.
                setTask((prev: any) => prev ? {
                  ...prev,
                  output_data: {
                    ...prev.output_data,
                    images: {
                      ...(prev.output_data?.images || {}),
                      hero: {
                        ...(prev.output_data?.images?.hero || {}),
                        urls: {
                          ...(prev.output_data?.images?.hero?.urls || {}),
                          large2x: _newUrl,
                          large: _newUrl,
                          original: _newUrl,
                        },
                      },
                    },
                  },
                } : prev);
              }
            }}
            onSave={async (updated) => {
              return await persistSynthesizedResult(updated, 'Social media posts saved successfully!');
            }}
            onCancel={() => {
              setEditedContent(editSourceOf(task));
              setIsEditing(false);
            }}
          />
        </div>
      );
    }

    // Email newsletter editor
    if (task?.input_data?.quick_win_type === 'email_newsletter') {
      return (
        <div style={{ height: '100vh', overflow: 'auto', background: 'var(--background, #ffffff)' }}>
          <EditableNewsletterViewer
            rawContent={editedContent}
            projectId={task?.project_id}
            onSave={async (updated) => {
              return await persistSynthesizedResult(updated, 'Newsletter saved successfully!');
            }}
            onCancel={() => {
              setEditedContent(editSourceOf(task));
              setIsEditing(false);
            }}
          />
        </div>
      );
    }

    // Default to article editor
    return (
      <div style={{ height: '100vh', overflow: 'auto', background: 'var(--background, #ffffff)' }}>
        <EditableArticleViewer
          markdownContent={editedContent}
          onSave={async (updatedMarkdown) => {
            return await persistSynthesizedResult(updatedMarkdown, 'Content saved successfully!');
          }}
          onCancel={() => {
            setEditedContent(editSourceOf(task));
            setIsEditing(false);
          }}
        />
      </div>
    );
  }

  // Not editing — two-section layout: header bar above, content card below
  const isEditableTitle =
    (task.input_data?.masterplan_stream && !task.input_data?.quick_win_type)
    || task.input_data?.quick_win_type === 'content_calendar'
    || task.input_data?.quick_win_type === 'faq_block'
    || task.input_data?.quick_win_type === 'content_block';

  return (
    <>
    <div className="preview-page">

      {/* ── Page header: title row + action row ──────────────────── */}
      <div className="preview-page-header">

        {/* Row 1: Title */}
        <div className="preview-page-title">
          {isEditableTitle ? (
            isEditingTitle ? (
              <div className="preview-title-edit">
                <input
                  className="preview-title-input"
                  value={titleDraft}
                  onChange={e => setTitleDraft(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && titleDraft.trim()) {
                      updateTask(task.task_id, { name: titleDraft.trim() })
                        .then(updated => { setTask(updated); setIsEditingTitle(false); })
                        .catch(() => {});
                    }
                    if (e.key === 'Escape') setIsEditingTitle(false);
                  }}
                  autoFocus
                />
                <GlassButton
                  pill
                  variant="primary"
                  disabled={!titleDraft.trim()}
                  onClick={() =>
                    updateTask(task.task_id, { name: titleDraft.trim() })
                      .then(updated => { setTask(updated); setIsEditingTitle(false); })
                      .catch(() => {})
                  }
                >Save</GlassButton>
                <GlassButton pill variant="secondary" onClick={() => setIsEditingTitle(false)}>Cancel</GlassButton>
              </div>
            ) : (
              <h1
                className="preview-title editable"
                onClick={() => { setTitleDraft(task.name); setIsEditingTitle(true); }}
                title="Click to edit title"
              >
                {task.name}
                <span className="title-edit-icon" aria-hidden="true"><Pencil size={14} /></span>
              </h1>
            )
          ) : (
            <h1 className="preview-title">{task.name}</h1>
          )}
        </div>

        {/* Row 2: Status badge + action buttons */}
        <div className="preview-page-actions">
          <GlassBadge variant={task.status === 'completed' ? 'success' : task.status === 'failed' ? 'danger' : 'warning'}>
            {task.status}
          </GlassBadge>

          <GlassButton
            pill size="sm"
            variant="secondary"
            onClick={() => setIsEditing(true)}
            icon={<Pencil size={13} />}
            title="Edit content"
          >
            Edit
          </GlassButton>

          {task.status === 'completed' && (
            <>
              <GlassButton
                pill size="sm"
                variant={userRating === 1 ? 'primary' : 'secondary'}
                onClick={() => handleRating(1)}
                icon={<ThumbsUp size={13} />}
                title="This content is good"
              />
              <GlassButton
                pill size="sm"
                variant={userRating === -1 ? 'danger' : 'secondary'}
                onClick={() => handleRating(-1)}
                icon={<ThumbsDown size={13} />}
                title="This content needs improvement"
              />
              <GlassButton
                pill size="sm"
                variant="secondary"
                onClick={() => handleExport('html')}
                icon={<FileDown size={13} />}
                title="Export as HTML"
              >
                HTML
              </GlassButton>
              <GlassButton
                pill size="sm"
                variant="secondary"
                onClick={() => handleExport('docx')}
                icon={<FileDown size={13} />}
                title="Export as Word document"
              >
                DOCX
              </GlassButton>
              <GlassButton
                pill size="sm"
                variant="secondary"
                onClick={() => setShowRegenerateModal(true)}
                icon={<RotateCcw size={13} />}
                title="Regenerate this content"
              >
                Regenerate
              </GlassButton>
            </>
          )}

          {/* Repurpose — articles only */}
          {task.status === 'completed' && task.input_data?.masterplan_stream && !task.input_data?.quick_win_type && (
            repurposeResult ? (
              <GlassButton pill size="sm" variant="secondary" disabled icon={<CheckCircle size={13} />}>
                Repurposed ({Object.values(repurposeResult.summary).filter(Boolean).length} assets)
              </GlassButton>
            ) : (
              <GlassButton
                pill size="sm"
                variant="primary"
                disabled={repurposing}
                icon={repurposing ? <Loader2 size={13} className="spinning" /> : <Share2 size={13} />}
                onClick={async () => {
                  if (!task.project_id || !task.task_id) return;
                  setRepurposing(true);
                  try {
                    const res = await repurposeArticle({ task_id: task.task_id, project_id: task.project_id });
                    setRepurposeResult(res);
                  } catch {
                    // silent — user can retry
                  } finally {
                    setRepurposing(false);
                  }
                }}
                title="Generate social posts, email drip, carousel, video script and pull quotes from this article"
              >
                {repurposing ? 'Repurposing...' : 'Repurpose for All Channels'}
              </GlassButton>
            )
          )}

          {/* Schedule publication — articles only (calendar tasks have their own
              sync flow; social types use SocialPublishModal) */}
          {task.status === 'completed' && task.input_data?.masterplan_stream && !task.input_data?.quick_win_type && (
            <GlassButton
              pill size="sm"
              variant="secondary"
              onClick={() => setShowScheduleModal(true)}
              icon={<CalendarCheck size={13} />}
              title="Schedule this article for publication (WordPress, Webflow, AiMe Sites)"
            >
              Schedule
            </GlassButton>
          )}

          {/* SEO metadata quick win — articles only */}
          {task.status === 'completed' && task.input_data?.masterplan_stream && !task.input_data?.quick_win_type && (
            seoTaskId ? (
              <GlassButton
                pill size="sm"
                variant="secondary"
                onClick={() => window.open(`/preview/${seoTaskId}`, '_blank')}
                icon={<CheckCircle size={13} />}
                title="Open the generated SEO metadata"
              >
                View SEO Metadata
              </GlassButton>
            ) : (
              <GlassButton
                pill size="sm"
                variant="secondary"
                disabled={generatingSeo}
                icon={generatingSeo ? <Loader2 size={13} className="spinning" /> : <Search size={13} />}
                onClick={async () => {
                  if (!task.task_id) return;
                  setGeneratingSeo(true);
                  try {
                    const seoTask = await createQuickWinMetaData(task.task_id);
                    setSeoTaskId(seoTask.task_id);
                  } catch {
                    // silent — user can retry (matches Repurpose behaviour)
                  } finally {
                    setGeneratingSeo(false);
                  }
                }}
                title="Generate SEO title, meta description and keywords for this article"
              >
                {generatingSeo ? 'Generating SEO...' : 'Generate SEO Metadata'}
              </GlassButton>
            )
          )}

          {/* Publish to social — multichannel types */}
          {task.status === 'completed' && ['social_media_posts', 'multichannel_thread', 'multichannel_carousel'].includes(task.input_data?.quick_win_type) && (
            taskExternalPostUrl ? (
              <a
                href={taskExternalPostUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.82rem', color: 'var(--primary)', textDecoration: 'none' }}
              >
                <ExternalLink size={13} /> View live
              </a>
            ) : task.input_data?.quick_win_type === 'multichannel_thread' ? (
              <GlassButton pill size="sm" variant="primary"
                onClick={() => { setPublishPlatformFilter('twitter'); setShowPublishModal(true); }}
                icon={<Send size={13} />}
              >
                Publish to Twitter
              </GlassButton>
            ) : task.input_data?.quick_win_type === 'multichannel_carousel' ? (
              <GlassButton pill size="sm" variant="primary"
                onClick={() => { setPublishPlatformFilter('linkedin'); setShowPublishModal(true); }}
                icon={<Send size={13} />}
              >
                Publish to LinkedIn
              </GlassButton>
            ) : (
              // social_media_posts — per-platform buttons
              <>
                {(['facebook', 'linkedin', 'twitter', 'instagram'] as const).map(platform => (
                  <GlassButton key={platform} pill size="sm" variant="primary"
                    onClick={() => { setPublishPlatformFilter(platform); setShowPublishModal(true); }}
                    icon={<Send size={13} />}
                    style={{ marginLeft: 4 }}
                  >
                    {platform.charAt(0).toUpperCase() + platform.slice(1)}
                  </GlassButton>
                ))}
              </>
            )
          )}

          <GlassButton pill size="sm" variant="secondary" onClick={() => window.close()} icon={<X size={13} />}>
            Close
          </GlassButton>
        </div>
      </div>

      {/* ── Content card ─────────────────────────────────────────── */}
      <GlassCard variant="default" padding="lg" className="content-preview">

        {/* Approval bar */}
        <div className="preview-approval-bar">
          <span className="preview-approval-label">Approval:</span>
          <span className={`preview-approval-badge ${approvalStatus || 'none'}`}>
            {approvalStatus === 'approved' && <><CheckCircle size={14} /> Approved</>}
            {approvalStatus === 'pending' && <><Clock size={14} /> Pending</>}
            {approvalStatus === 'rejected' && <><XCircle size={14} /> Rejected</>}
            {!approvalStatus && <><Circle size={14} /> Not Reviewed</>}
          </span>
          <div className="preview-approval-actions">
            <GlassButton
              variant={approvalStatus === 'approved' ? 'primary' : 'secondary'}
              onClick={async () => {
                const newStatus = approvalStatus === 'approved' ? null : 'approved';
                await updateTaskApproval(taskId!, newStatus);
                setApprovalStatus(newStatus);
              }}
              icon={<CheckCircle size={16} />}
            >
              Approve
            </GlassButton>
            <GlassButton
              variant={approvalStatus === 'rejected' ? 'danger' : 'secondary'}
              onClick={async () => {
                const newStatus = approvalStatus === 'rejected' ? null : 'rejected';
                await updateTaskApproval(taskId!, newStatus);
                setApprovalStatus(newStatus);
              }}
              icon={<XCircle size={16} />}
            >
              Reject
            </GlassButton>
          </div>
        </div>

        {task.input_data?.quick_win_type === 'content_calendar' && syncStatus && (
          <div className="sync-status-table-wrap">
            <table className="sync-status-table">
              <thead>
                <tr>
                  <th>Approved</th>
                  <th>Scheduled</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{syncStatus.approved_articles}</td>
                  <td>{syncStatus.scheduled_publications}</td>
                  <td>{syncStatus.total_articles}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

      {showResyncModal && resyncPreview && (
        <div className="resync-modal-overlay" onClick={() => setShowResyncModal(false)}>
          <div className="resync-modal" onClick={e => e.stopPropagation()}>
            <div className="resync-modal-header">
              <h2>Re-sync Preview</h2>
              <button className="resync-modal-close" onClick={() => setShowResyncModal(false)} title="Close">
                <X size={18} />
              </button>
            </div>
            <div className="resync-modal-body">
              {resyncPreview.updated.length === 0 &&
               resyncPreview.cancelled.length === 0 &&
               resyncPreview.added.length === 0 &&
               resyncPreview.unchanged.length > 0 && (
                <p className="resync-no-changes">Everything is already in sync — no changes needed.</p>
              )}
              {resyncPreview.updated.length === 0 &&
               resyncPreview.cancelled.length === 0 &&
               resyncPreview.added.length === 0 &&
               resyncPreview.unchanged.length === 0 && (
                <p className="resync-no-changes">No scheduled publications found to sync.</p>
              )}
              {resyncPreview.updated.length > 0 && (
                <div className="resync-section">
                  <h3>Update ({resyncPreview.updated.length})</h3>
                  <ul>
                    {resyncPreview.updated.map((item, i) => (
                      <li key={i}>
                        <span className="resync-title">{item.title}</span>
                        <span className="resync-date-change">{item.old_date} → {item.new_date}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {resyncPreview.cancelled.length > 0 && (
                <div className="resync-section">
                  <h3>Cancel ({resyncPreview.cancelled.length})</h3>
                  <ul>
                    {resyncPreview.cancelled.map((item, i) => (
                      <li key={i}>
                        <span className="resync-title">{item.title}</span>
                        <span className="resync-reason">Unapproved in calendar</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {resyncPreview.added.length > 0 && (
                <div className="resync-section">
                  <h3>Add ({resyncPreview.added.length})</h3>
                  <ul>
                    {resyncPreview.added.map((item, i) => (
                      <li key={i}>
                        <span className="resync-title">{item.title}</span>
                        <span className="resync-date-change">{item.scheduled_for}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {resyncPreview.unchanged.length > 0 && (
                <div className="resync-section resync-unchanged">
                  <h3>No Change ({resyncPreview.unchanged.length})</h3>
                </div>
              )}
            </div>
            <div className="resync-modal-footer">
              {(resyncPreview.updated.length > 0 || resyncPreview.cancelled.length > 0 || resyncPreview.added.length > 0) ? (
                <>
                  <GlassButton
                    variant="primary"
                    onClick={handleApplyResync}
                    disabled={isResyncing}
                    loading={isResyncing}
                    icon={<Check size={16} />}
                  >
                    {isResyncing ? 'Applying…' : 'Apply Changes'}
                  </GlassButton>
                  <GlassButton variant="secondary" onClick={() => setShowResyncModal(false)} disabled={isResyncing}>
                    Cancel
                  </GlassButton>
                </>
              ) : (
                <GlassButton variant="secondary" onClick={() => setShowResyncModal(false)}>
                  Close
                </GlassButton>
              )}
            </div>
          </div>
        </div>
      )}

      {showRegenerateModal && (
        <div className="resync-modal-overlay" onClick={() => setShowRegenerateModal(false)}>
          <div className="resync-modal" onClick={e => e.stopPropagation()}>
            <div className="resync-modal-header">
              <h2>Regenerate Content</h2>
              <button className="resync-modal-close" onClick={() => setShowRegenerateModal(false)} title="Close">
                <X size={18} />
              </button>
            </div>
            <div className="resync-modal-body">
              <p style={{ margin: '0 0 12px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                A new version of this content will be generated using the same brief. Optionally provide feedback to guide the regeneration.
              </p>
              <textarea
                className="regenerate-feedback-textarea"
                placeholder="Optional: What should be improved? (e.g. 'Make it more casual', 'Add more stats')"
                value={regenerateFeedback}
                onChange={e => setRegenerateFeedback(e.target.value)}
                rows={4}
              />
            </div>
            <div className="resync-modal-footer">
              <GlassButton
                variant="primary"
                onClick={() => handleRegenerate()}
                disabled={isRegenerating}
                loading={isRegenerating}
                icon={<RotateCcw size={16} />}
              >
                {isRegenerating ? 'Regenerating…' : 'Regenerate'}
              </GlassButton>
              <GlassButton variant="secondary" onClick={() => setShowRegenerateModal(false)} disabled={isRegenerating}>
                Cancel
              </GlassButton>
            </div>
          </div>
        </div>
      )}

      <div className="preview-body">
        {renderHeroImage()}
        {(() => {
          const grounding = task?.output_data?.grounding;
          const taskGroundingMode = task?.input_data?.grounding_mode;
          // Research was requested (mode not off) but the pipeline didn't
          // ground — either auto's recency heuristic vetoed it ("evergreen")
          // or the grounded call failed / returned no usable sources.
          const researchSkipped = !!(grounding && !grounding.grounded && taskGroundingMode && taskGroundingMode !== 'off');
          const skippedEvergreen = researchSkipped && (grounding?.note || '').includes('not recency-sensitive');
          if (!grounding?.grounded && !researchSkipped && !task?.output_data?.humanization && !task?.output_data?.readability) return null;
          return (
          <div className="grounding-block">
            <div className="grounding-badges">
              {grounding?.grounded && (
                <div className="grounding-badge" title={grounding.note || 'Grounded via live sources'}>
                  <Globe size={14} className="grounding-badge__icon" />
                  Grounded via {grounding.sources?.length || 0} source{(grounding.sources?.length !== 1) ? 's' : ''}
                </div>
              )}
              {researchSkipped && (
                <div
                  className="grounding-badge"
                  title={`${grounding?.note || 'No live sources were used'} — use "Regenerate with Research" to force live sources.`}
                >
                  <Globe size={14} className="grounding-badge__icon" />
                  {skippedEvergreen
                    ? 'Research skipped — topic assessed as evergreen'
                    : 'Research did not complete — generated from model knowledge'}
                </div>
              )}
              {researchSkipped && task?.status === 'completed' && (
                <GlassButton
                  pill size="sm"
                  variant="secondary"
                  disabled={isRegenerating}
                  icon={isRegenerating ? <Loader2 size={13} className="spinning" /> : <RotateCcw size={13} />}
                  onClick={() => handleRegenerate(true)}
                  title="Regenerate this article with live web research (Google Search grounding) regardless of the evergreen assessment"
                >
                  {isRegenerating ? 'Regenerating…' : 'Regenerate with Research'}
                </GlassButton>
              )}
              {task?.output_data?.humanization && (
                <div
                  className="grounding-badge"
                  title={
                    task.output_data.humanization.rewritten
                      ? `Machine-style patterns detected (score ${task.output_data.humanization.score_before}/100, lower is better) — automatically rewritten to ${task.output_data.humanization.score_after}/100.`
                        + (task.output_data.humanization.top_phrases?.length
                          ? ` Removed: ${task.output_data.humanization.top_phrases.join(', ')}`
                          : '')
                      : `Style score ${task.output_data.humanization.score_before}/100 (lower is better) — below the rewrite threshold, no rewrite needed.`
                  }
                >
                  <Wand2 size={14} className="grounding-badge__icon" />
                  {task.output_data.humanization.rewritten
                    ? `Style pass ${task.output_data.humanization.score_before} → ${task.output_data.humanization.score_after}`
                    : `Style score ${task.output_data.humanization.score_before}`}
                </div>
              )}
              {task?.output_data?.readability && (
                <div
                  className="grounding-badge"
                  title={(() => {
                    const r = task.output_data.readability;
                    const parts: string[] = [];
                    if (r.rewritten && r.score_before != null) parts.push(`Editorial rewrite lifted readability ${r.score_before} → ${r.score}.`);
                    if (r.flesch != null) parts.push(`Flesch Reading Ease ${r.flesch}.`);
                    const details = r.details ? Object.values(r.details).filter(Boolean) : [];
                    parts.push(details.length ? `Remaining: ${details.join('; ')}` : 'All measured readability checks passed.');
                    return parts.join(' ');
                  })()}
                >
                  <BookOpen size={14} className="grounding-badge__icon" />
                  Readability {task.output_data.readability.score}
                  {task.output_data.readability.failing?.length > 0 && ` (${task.output_data.readability.failing.length} to review)`}
                </div>
              )}
            </div>
            {Array.isArray(task?.output_data?.grounding?.sources) && task.output_data.grounding.sources.length > 0 && (
              <ol className="grounding-sources">
                {task.output_data.grounding.sources.map((s: { title?: string; url?: string }, i: number) => (
                  <li key={i} className="grounding-source">
                    {s.url ? (
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="grounding-source__link">
                        {s.title || s.url}
                      </a>
                    ) : (
                      <span>{s.title || 'Untitled source'}</span>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </div>
          );
        })()}
        {renderContent()}

        {/* Always-present invisible JSON-LD script tag for view-source visibility */}
        {task?.schema_jsonld_html && (
          <div
            style={{ display: 'none' }}
            dangerouslySetInnerHTML={{ __html: task.schema_jsonld_html }}
          />
        )}

        {/* Collapsible Schema markup disclosure panel */}
        <div className="content-preview-schema-tab">
          <button
            className="content-preview-schema-disclosure-toggle"
            onClick={() => setSchemaExpanded(prev => !prev)}
            aria-expanded={schemaExpanded}
          >
            <span>Schema markup</span>
            {schemaExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {schemaExpanded && (
            <div className="content-preview-schema-disclosure-body">
              {schemaLoading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                  <Loader2 size={16} className="spin" /> Loading schema…
                </div>
              )}

              {!schemaLoading && schemaPreview && schemaPreview.counts.valid === 0 && (
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', padding: '8px 0' }}>
                  <p>No schema applicable for this content type.</p>
                  {schemaPreview.dropped.length > 0 && (
                    <details style={{ marginTop: 8 }}>
                      <summary style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}>Why?</summary>
                      <ul style={{ marginTop: 6, paddingLeft: 20 }}>
                        {schemaPreview.dropped.map((d, i) => (
                          <li key={i} style={{ fontSize: '0.8rem' }}>{d.type}: {d.reason}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}

              {!schemaLoading && schemaPreview && schemaPreview.counts.valid > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <GlassButton
                      variant="secondary"
                      onClick={() => {
                        navigator.clipboard.writeText(schemaPreview.html);
                        setSchemaCopied(true);
                        setTimeout(() => setSchemaCopied(false), 1500);
                      }}
                    >
                      <Copy size={14} />
                      {schemaCopied ? 'Copied!' : 'Copy'}
                    </GlassButton>
                  </div>
                  <pre className="content-preview-schema-block">
                    {schemaPreview.html}
                  </pre>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {showPublishModal && task && (() => {
        // Parse social content for the modal
        let platformContent: Record<string, any> = {};
        try {
          let text = task.synthesized_result || '';
          const fenceMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/);
          if (fenceMatch) text = fenceMatch[1].trim();
          let parsed = JSON.parse(text);
          if (parsed?.response) {
            const inner = parsed.response;
            const fenceMatch2 = typeof inner === 'string' ? inner.match(/```(?:json)?\s*([\s\S]*?)```/) : null;
            parsed = fenceMatch2 ? JSON.parse(fenceMatch2[1].trim()) : (typeof inner === 'string' ? JSON.parse(inner) : inner);
          }
          // Normalize platformContent per task type
          const qwType = task.input_data?.quick_win_type;
          if (qwType === 'multichannel_thread') {
            platformContent = { twitter: { post: parsed?.tweets?.[0]?.text ?? '' } };
          } else if (qwType === 'multichannel_carousel') {
            const parts = [parsed?.title, parsed?.subtitle, parsed?.cover_text].filter(Boolean);
            platformContent = { linkedin: { post: parts.join('\n\n') } };
          } else {
            platformContent = parsed;  // social_media_posts — pass directly
          }
        } catch { /* ignore parse errors */ }
        return (
          <SocialPublishModal
            taskId={taskId!}
            projectId={task.project_id}
            platformContent={platformContent}
            platformFilter={publishPlatformFilter}
            onSuccess={(postUrl) => setTaskExternalPostUrl(postUrl)}
            onClose={() => { setShowPublishModal(false); setPublishPlatformFilter(undefined); }}
          />
        );
      })()}

      {showScheduleModal && task && taskId && task.project_id && (
        <SchedulePublicationModal
          isOpen={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          taskId={taskId}
          projectId={task.project_id}
        />
      )}

      {/* ── Internal Links Panel — shown for article tasks ────────── */}
      {task && !task.input_data?.quick_win_type && taskId && (
        <InternalLinksPanel
          taskId={taskId}
          onApplied={loadTask}
        />
      )}

      </GlassCard>
    </div>

    {cascadeDialogOpen && (
      <div
        className="cascade-dialog-overlay"
        onClick={handleCascadeDismiss}
        role="dialog"
        aria-label="Apply image to derived social posts"
        aria-modal="true"
      >
        <div className="cascade-dialog" onClick={e => e.stopPropagation()}>
          <p className="cascade-dialog-heading">Apply this image to all derived social posts as well?</p>
          <p className="cascade-dialog-count">
            {pendingChildCount} social post set{pendingChildCount !== 1 ? 's' : ''} will be updated
          </p>
          {cascadeError && <p className="cascade-dialog-error">{cascadeError}</p>}
          <div className="cascade-dialog-actions">
            <GlassButton variant="primary" size="sm" onClick={handleCascadeConfirm} disabled={isCascading} loading={isCascading}>
              All derived posts
            </GlassButton>
            <GlassButton variant="secondary" size="sm" onClick={handleCascadeDismiss} disabled={isCascading}>
              Just this article
            </GlassButton>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ContentPreview;
